import { Injectable } from '@angular/core';
import { EMPTY, Observable, of, OperatorFunction, TimeoutError, timer, throwError } from 'rxjs';
import { catchError, delay, filter, first, retryWhen, scan, switchMap, tap, timeout } from 'rxjs/operators';
import { HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { PipelineStageConfig, StatusObject } from '../../interfaces/biozoom-api/pipeline';
import { BiozoomError } from '../../interfaces/scanner-api/biozoom-error';
import { Router } from '@angular/router';
import { Insomnia } from '@ionic-native/insomnia/ngx';
import { Platform } from '@ionic/angular';
import { PreciseAoxMeasurementResult } from '../../interfaces/biozoom-api/precise-aox-measurement-result';
import { ScanGenericService } from './scan-generic.service';
import { getResult, resultToString } from '../../methods/result';
import { State } from '../../interfaces/biozoom-api/app-configuration';
import { environment } from '../../../environments/environment';
import { ScannerStatus } from '../../interfaces/scanner-api/scanner-status';
import { logError } from '../../methods/error';
import { AoxMeasurementResult } from '../../interfaces/biozoom-api/aox-measurement-result';
import { ScannerApiService } from '../scanner/scanner-api.service';
import { FileManagerService } from '../fileManager/file-manager.service';
import { getLogs } from '../../methods/send-logs';
import {
  checkScannerBattery, getKeyVal,
  getScannerSCNModelFromSerialNumber, roundValueToTwoDigests,
  ScannerSCNModel,
  updateESPNeeded,
  updateInfNeeded
} from '../../methods/helpers';
import { processMeasurementPreciseAox } from '../../methods/measurement';
import {addMeasurementToLicense, sendLicense} from "../../methods/license";
import { getServerDisplayName } from '../../methods/server-display';
import { getUploadHttpStatus, logUploadNetworkSummary } from '../../methods/upload-network-log';

const _ = require('lodash');

@Injectable({
  providedIn: 'root'
})
export class ScanGenericSinglesService {

  constructor(
    private scanGenericService: ScanGenericService,
    private apiService: ScannerApiService,
    private router: Router,
    private insomnia: Insomnia,
    private platform: Platform,
    private fileManagerService: FileManagerService,
  ) { }

  /**
   * reset all measurement results and temporary values
   * useful as a cleanup after a measurement, e.g. on the home screen
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   * @param exit if true, call the exit stage
   */
  resetAll = (pipelineConfig: PipelineStageConfig, exit = false): OperatorFunction<StatusObject, boolean> => {
    return switchMap((stati: StatusObject) => {
      pipelineConfig.resetAll();
      if (exit) {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      }
      return of(true);
    });
  }

  deleteFlash = (
    pipelineConfig: PipelineStageConfig
  ) => {
    return switchMap((_stati: StatusObject): Observable<boolean> => {
      return of({}).pipe(
        switchMap(async (_stati: StatusObject) => {
          const shouldUpdate = updateESPNeeded(
            pipelineConfig?.state?.persistentValues?.SCANNER_SERIAL,
            pipelineConfig.state.persistentValues?.SCANNER_STATUS?.wifi_firmware ?? '',
            pipelineConfig.config
          ) || updateInfNeeded(
            pipelineConfig?.state?.persistentValues?.SCANNER_VERSION,
            pipelineConfig?.state?.persistentValues?.SCANNER_STATUS?.sensor_firmware ?? '',
            pipelineConfig?.state?.persistentValues?.SCANNER_STATUS?.wifi_firmware ?? '',
            pipelineConfig.config);
          if (shouldUpdate || true) {
            await this.scanGenericService.deleteFlash(pipelineConfig);
          }
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
          return true;
        })
      );
    });
  }

  /**
   * attempt to connect to the registered scanner
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  connectScanner = (
    pipelineConfig: PipelineStageConfig
  ) => {
    return switchMap((_stati: StatusObject): Observable<boolean> => {
      const validateSerial = !(pipelineConfig.stageConfig.validateSerial === false);
      return this.scanGenericService.connectToScanner(
        pipelineConfig.setState,
        pipelineConfig.setStep,
        // pipelineConfig.setBlockScreen,
        pipelineConfig.scanner,
        pipelineConfig.state.persistentValues,
        validateSerial,
      ).pipe(
        switchMap((r) => {
          if (r?.status) {
            if (!pipelineConfig.state.persistentValues?.DEMO_MODE && (r?.serial !== pipelineConfig.scanner?.serial) && validateSerial) {
              pipelineConfig.executeStage(
                pipelineConfig?.stageConfig?.eventTargets?.onErrorWrongScanner,
                'Wrong Scanner',
                pipelineConfig.stage
              );
              return of([false, '']);
            }
            return this.scanGenericService.getStatus(
              pipelineConfig.scanner,
              pipelineConfig.state.persistentValues,
              pipelineConfig.setState
            ).pipe(
              switchMap((e) => {
                pipelineConfig.setStateObj('_statusBefore', e);
                return of([true, e]);
              })
            );
          } else {
            switch (r?.error) {
              case BiozoomError.BATTERY: {
                pipelineConfig.executeStage(
                  pipelineConfig?.stageConfig?.eventTargets?.onErrorBattery ?? r.error,
                  'BatteryError',
                  pipelineConfig.stage
                );
                break;
              }
              case BiozoomError.SCANNER: {
                pipelineConfig.executeStage(
                  pipelineConfig?.stageConfig?.eventTargets?.onErrorScanner ?? r.error,
                  'ScannerError',
                  pipelineConfig.stage
                );
                break;
              }
              case BiozoomError.WIFI: {
                pipelineConfig.executeStage(
                  pipelineConfig?.stageConfig?.eventTargets?.onErrorWifi ?? r.error,
                  'WifiError',
                  pipelineConfig.stage
                );
                break;
              }
              case BiozoomError.USB: {
                pipelineConfig.executeStage(
                  pipelineConfig?.stageConfig?.eventTargets?.onErrorUSB ?? r.error,
                  'USBError',
                  pipelineConfig.stage
                );
                break;
              }
              default: {
                pipelineConfig.executeStage(
                  r.error,
                  'error',
                  pipelineConfig.stage
                );
                break;
              }
            }
            return of([false, {}]);
          }
        }),
        switchMap(async (e: [boolean, ScannerStatus]) => {
          if (e[0]) {
            if (e[1]?.memory_healthy !== undefined && !(e[1]?.memory_healthy)) {
              await this.scanGenericService.deleteFlash(pipelineConfig);
            }
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
          }
          return true;
        })
      );
    });
  }

  /**
   * validate whether the status retrieved from the 'connectScanner' stage satisfies the app's firmware requirements
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  checkScannerFirmware = (
    pipelineConfig: PipelineStageConfig,
  ): OperatorFunction<StatusObject, boolean> => {
    return switchMap((stati: StatusObject) => {
      return this.scanGenericService.checkScannerFirmware(stati._statusBefore, pipelineConfig.getState(State.USB_MODE), pipelineConfig.config).pipe(
        switchMap((r) => {
          if (r.status) {
            if (checkScannerBattery(r.scannerStatus, pipelineConfig.state.appSettings.batteryCutoffVoltage)) {
              pipelineConfig.executeStage(
                pipelineConfig?.stageConfig?.eventTargets?.onSuccess,
                'onSuccess',
                pipelineConfig.stage
              );
            } else {
              pipelineConfig.executeStage(
                pipelineConfig?.stageConfig?.eventTargets?.onErrorBattery ?? r.error,
                'BatteryError',
                pipelineConfig.stage
              );
            }
            return of(true);
          } else {
            if (r.error === BiozoomError.PRODUCT) {
              pipelineConfig.executeStage(
                pipelineConfig?.stageConfig?.eventTargets?.onErrorProduct ?? r.error,
                'ProductError',
                pipelineConfig.stage
              );
            } else if (r.error === BiozoomError.OUTDATED) {
              pipelineConfig.executeStage(
                pipelineConfig?.stageConfig?.eventTargets?.onErrorOutdated ?? r.error,
                'Outdated',
                pipelineConfig.stage
              );
            }
            return of(false);
          }
        })
      );
    });
  }

  /**
   * wait until the user's hand is recognized by the scanner
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  handOnRecognition = (
    pipelineConfig: PipelineStageConfig
  ): OperatorFunction<StatusObject, boolean> => {
    // tslint:disable-next-line:variable-name
    return switchMap((_stati: StatusObject) => {
      const handOnRecognitionDelay = Number(
        (pipelineConfig.state.persistentValues as Record<string, any>)?.handOnRecognitionDelay ??
        100
      );
      return of({}).pipe(
        switchMap(() => {
          return this.scanGenericService.getStatus(pipelineConfig.scanner, pipelineConfig.state.persistentValues, pipelineConfig.setState);
        }),
        first(),
        tap((el: ScannerStatus | {}) => {
          // store the scanner status as a pipeline input
          pipelineConfig.setStateObj('_statusBefore', el);
          pipelineConfig.setState('_statusBefore', el, 'temp');
        }),
        delay(Number.isFinite(handOnRecognitionDelay) ? handOnRecognitionDelay : 100),
        switchMap((scanner) => {
          return this.scanGenericService.startHandRecognition(
            pipelineConfig.state.persistentValues, pipelineConfig.state.appSettings, _stati?._statusBefore?.wifi_firmware ?? '0'
          );
        }),
        filter((el: boolean) => !!el),
        first(),
        delay(100),
        tap((_) => {
          // store the scanner status as a pipeline input
          // pipelineConfig.setStateObj('_statusBefore', el);
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        })
      );
    });
  }

  /**
   * wait until the user's hand is no longer recognized by the scanner
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  handOffRecognition = (
    pipelineConfig: PipelineStageConfig
  ): OperatorFunction<StatusObject, boolean> => {
    // tslint:disable-next-line:variable-name
    return switchMap((_stati: StatusObject) => {
      return of({}).pipe(
        switchMap(() => {
          return this.scanGenericService.getStatus(pipelineConfig.scanner, pipelineConfig.state.persistentValues, pipelineConfig.setState);
        }),
        first(),
        tap((el: ScannerStatus | {}) => {
          // store the scanner status as a pipeline input
          pipelineConfig.setStateObj('_statusAfter', el);
          pipelineConfig.setState('_statusAfter', el, 'temp');
        }),
        delay(100),
        switchMap((scanner) => {
          return this.scanGenericService.startHandOffRecognition(
            pipelineConfig.state.persistentValues, pipelineConfig.state.appSettings, _stati?._statusBefore?.wifi_firmware ?? '0'
          );
        }),
        filter((el: boolean) => !!el),
        first(),
        delay(100),
        tap((_) => {
          // store the scanner status as a pipeline input
          // pipelineConfig.setStateObj('_statusAfter', el);
          pipelineConfig.setStep({
            idx: pipelineConfig.reduceQueries ? pipelineConfig.getStep().idx + 1 : undefined
          });
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        })
      );
    });
  }

  /**
   * check whether sufficient measurements have been recorded.
   * if this is true, proceed to the onSucceed-stage.
   * otherwise, go to the onRepeat-stage.
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  repeatMeasurement = (pipelineConfig: PipelineStageConfig): OperatorFunction<StatusObject, boolean> => {
    return switchMap((stati: StatusObject) => {
      const numMeasurements = pipelineConfig.getStep().numMeasurements;
      const currentMeasurements = stati._measurements.length ?? 0;

      if (numMeasurements > currentMeasurements) {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onRepeat, 'onRepeat', pipelineConfig.stage);
      } else {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      }
      return of(true);
    });
  }

  /**
   * attempt to connect to a server
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  connectToServer = (
    pipelineConfig: PipelineStageConfig
  ): OperatorFunction<StatusObject, boolean> => {
    return switchMap((_stati: StatusObject) => {
      pipelineConfig.setStep({
        idx: pipelineConfig.getStep().aox.length,
      });
      if (pipelineConfig.reduceQueries) {
        pipelineConfig.resetProgress();
      }

      let count = 0;

      return this.scanGenericService.disconnectAndConnectToServer(pipelineConfig.state.persistentValues).pipe(
        timeout(60000),
        first(),
        delay(1000),
        switchMap(() => {
          return timer(0, 200).pipe(
            // ensure that the current connection type is the default one
            switchMap(() => {
              count += 1;
              return of(
                pipelineConfig.state.persistentValues.SKIP_WAIT_FOR_CONNECTION
                || (pipelineConfig.networkType === pipelineConfig.getNetworkType() || count > 100)
              );

            }),
            filter(el => !!el),
            first(),
          );
        }),
        switchMap((status) => {
          if (status) {
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
            return of(true);
          } else {
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onErrorInternet ?? 'internetMightNotBeAvailable', 'InternetError', pipelineConfig.stage);
            return of(false);
          }
        }),
        catchError((_err) => {
          if (typeof _err === 'object') {
            logError(_err);
          }
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onErrorInternet ?? 'internetMightNotBeAvailable', 'InternetError', pipelineConfig.stage);
          return of(false);
        })
      );
    });
  }

  /**
   * check whether sufficient results have been recorded.
   * if this is true, proceed to the onSucceed-stage.
   * otherwise, go to the onRepeat-stage.
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  repeatUpload = (pipelineConfig: PipelineStageConfig): OperatorFunction<StatusObject, boolean> => {
    return switchMap((stati: StatusObject) => {
      const numMeasurements = stati._measurements.length;
      let currentMeasurements = stati._aoxData.length;
      if (currentMeasurements === undefined) {
        currentMeasurements = stati._hrvData.length ?? 0;
      }
      // if more measurements than uploads exist, proceed with the onRepeat-stage
      if (numMeasurements > currentMeasurements) {
        pipelineConfig.executeStage(
          pipelineConfig?.stageConfig?.eventTargets?.onRepeat,
          'onRepeat',
          pipelineConfig.stage
        );
      } else {
        // if the amount of stored results is lower than the required amount of measurements, proceed with the onInsufficient-stage
        if (pipelineConfig.getStep().numMeasurements > currentMeasurements) {
          pipelineConfig.executeStage(
            pipelineConfig?.stageConfig?.eventTargets?.onInsufficientMeasurements,
            'onInsufficientMeasurements',
            pipelineConfig.stage
          );
        } else {
          pipelineConfig.executeStage(
            pipelineConfig?.stageConfig?.eventTargets?.onSuccess,
            'onSuccess',
            pipelineConfig.stage
          );
        }
      }
      return of(true);
    });
  }

  /**
   * delete the latest upload, as well as the corresponding measurement
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  deleteResult = (pipelineConfig: PipelineStageConfig): OperatorFunction<StatusObject, boolean> => {
    return switchMap((stati: StatusObject) => {
      const newAOX = _.cloneDeep(stati._aoxData);
      const oldRes = newAOX.splice(stati._aoxData.length - 1, 1);
      const oldResArr = pipelineConfig.state.temp?.AOX_DATA_DELETED ?? [];
      const newResArr = [...oldResArr, ...oldRes];
      pipelineConfig.setState('AOX_DATA_DELETED', newResArr, 'temp');
      // set the new aox data, as well as deleting the measurement at the corresponding index
      // if the last of 4 uploads will be deleted, the new length will be 3, which is the index
      // of the 4th measurement.
      pipelineConfig.setStep({
        idx: stati._aoxData.length,
        aoxData: newAOX,
        delete: true
      });
      // pipelineConfig.setState(State.AOX_MEASUREMENT_RESULTS, newAOX);
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }

  getErrorDict = (aoxData: AoxMeasurementResult[]) => {
    const reply = aoxData.filter((el) => !!el.error_prediction).map((el) => el.error_prediction);
    return reply.length > 0 ? reply : undefined;
  }

  // requires a valid aox_result
  compareResultToReferenceScanners = (pipelineConfig: PipelineStageConfig): OperatorFunction<StatusObject, boolean> => {
    return switchMap((stati: StatusObject) => {

      const currentAoxResult = pipelineConfig.state.temp.result.precise_aox ?? 0;
      const currentAoxResultStd = pipelineConfig.state.temp.result.precise_aox_std ?? 0;
      const scannerModel: ScannerSCNModel = pipelineConfig.state.temp.result.model ?? '';
      const referenceScanners: Record<string, string> = pipelineConfig.state.temp.referenceSerialNumbers ?? {};
      const referenceScannerResults: Record<string, AoxMeasurementResult[]> = pipelineConfig.state.temp.resultReferenceScanners ?? {};

      if (currentAoxResult === 0 || currentAoxResultStd === 0 || referenceScanners === {} || referenceScannerResults === {}) {
        pipelineConfig.executeStage(
          pipelineConfig?.stageConfig?.eventTargets?.onFail,
          'onFail',
          pipelineConfig.stage
        );
        return of(true);
      }

      // set deviation limits, can be overwritten with config or fallback to default values
      const deviationMinLimit = pipelineConfig.state.temp.deviationMinLimit ?? 0.8;
      const deviationAvgLimit = pipelineConfig.state.temp.deviationAvgLimit ?? 0.5;
      const deviationMaxLimit = pipelineConfig.state.temp.deviationMaxLimit ?? 0.8;

      const referenceScannerResultsBySerial: Record<string, {
        // serial: string;
        aox: number;
        aox_std: number;
        time: string;
      }> = {};

      const referenceScannerMRSRRSSCN05Results: Record<string, {
        aox: number;
        aox_std: number;
        time: string;
      }> = {};

      const referenceScannerMRSRRSSCN06Results: Record<string, {
        aox: number;
        aox_std: number;
        time: string;
      }> = {};

      // populate referenceScannerResultsBySerial
      for (const key of Object.keys(referenceScannerResults)) {
        const result = referenceScannerResults[key];
        const aoxResult = getKeyVal('aox', result);
        const serial = getKeyVal('serial', result);
        const aoxStd = getKeyVal('aox_std', result);
        const time = getKeyVal('storage_timestamp', result);

        referenceScannerResultsBySerial[serial] = {aox: aoxResult, aox_std: aoxStd, time};

      }

      let idxSCN05 = 0;
      let idxSCN06 = 0;

      // populate referenceScanner with the SCN version
      for (const [idx, serial] of Object.values(referenceScanners).entries()) {

        const currentSCNVersion = getScannerSCNModelFromSerialNumber(serial);

        switch (currentSCNVersion) {
          case 'MRSRRS_SCN05':
            referenceScannerMRSRRSSCN05Results[idxSCN05] = {
              aox: referenceScannerResultsBySerial[serial].aox,
              aox_std: referenceScannerResultsBySerial[serial].aox_std,
              time: referenceScannerResultsBySerial[serial].time,
            };
            idxSCN05++;
            break;
          case 'MRSRRS_SCN06':
            referenceScannerMRSRRSSCN06Results[idxSCN06] = {
              aox: referenceScannerResultsBySerial[serial].aox,
              aox_std: referenceScannerResultsBySerial[serial].aox_std,
              time: referenceScannerResultsBySerial[serial].time,
            };
            idxSCN06++;
            break;
          default:
            pipelineConfig.executeStage(
              pipelineConfig?.stageConfig?.eventTargets?.onUnrecognizedScannerModel,
              'onUnrecognizedScannerModel',
              pipelineConfig.stage,
            );
            return of(true);
        }
      }

      // get min and max from current scanner
      const currentAoxMin = currentAoxResult - (currentAoxResultStd) / 2;
      const currentAoxMax = currentAoxResult + (currentAoxResultStd) / 2;
      pipelineConfig.setState('currentAoxMin', roundValueToTwoDigests(currentAoxMin), 'temp');
      pipelineConfig.setState('currentAoxMax', roundValueToTwoDigests(currentAoxMax), 'temp');

      // calculate mean and std for reference Scanners
      // case SCN05
      let refValueMRSRRSSCN05 = 0;
      let refValueMRSRRSSCN05Std = 0;
      let refMRSRRSSCN05OldestTimestamp = new Date(referenceScannerMRSRRSSCN05Results[0]?.time ?? 0);

      // get average values of reference scanners
      let countMRSRRSSCN05 = 0;
      for (const values of Object.values(referenceScannerMRSRRSSCN05Results)){
        refValueMRSRRSSCN05 += values.aox;
        refValueMRSRRSSCN05Std += values.aox_std ** 2;
        refMRSRRSSCN05OldestTimestamp =
          new Date(values.time) < refMRSRRSSCN05OldestTimestamp ? new Date(values.time) : refMRSRRSSCN05OldestTimestamp;
        countMRSRRSSCN05++;
      }

      refValueMRSRRSSCN05 = refValueMRSRRSSCN05 / countMRSRRSSCN05;
      refValueMRSRRSSCN05Std = refValueMRSRRSSCN05Std ** (1 / 2) / countMRSRRSSCN05;
      const refMRSRRSSCN05OldestTimestampInSeconds = Math.floor(new Date(refMRSRRSSCN05OldestTimestamp).getTime() / 1000);

      // case SCN06
      let refValueMRSRRSSCN06 = 0;
      let refValueMRSRRSSCN06Std = 0;
      let refMRSRRSSCN06OldestTimestamp = new Date(referenceScannerMRSRRSSCN06Results[0]?.time ?? 0);

      // get average values of reference scanners
      let countMRSRRSSCN06 = 0;
      for (const values of Object.values(referenceScannerMRSRRSSCN06Results)){
        refValueMRSRRSSCN06 += values.aox;
        refValueMRSRRSSCN06Std += values.aox_std ** 2;
        refMRSRRSSCN06OldestTimestamp =
          new Date(values.time) < refMRSRRSSCN06OldestTimestamp ? new Date(values.time) : refMRSRRSSCN06OldestTimestamp;
        countMRSRRSSCN06++;
      }

      refValueMRSRRSSCN06 = refValueMRSRRSSCN06 / countMRSRRSSCN06;
      refValueMRSRRSSCN06Std = refValueMRSRRSSCN06Std ** (1 / 2) / countMRSRRSSCN06;
      const refMRSRRSSCN06OldestTimestampInSeconds = Math.floor(new Date(refMRSRRSSCN06OldestTimestamp).getTime() / 1000);

      // handle error criteria, propagate error by time
      const measurementTimestampInSeconds = Math.floor(pipelineConfig.state.temp.result.localTimestamp / 1000) ?? 0;
      let deltaT = 0;
      // let sigmaIncrease = 0;

      let sigmaIncreaseMRSRRSSCN05 = 0;
      let sigmaIncreaseMRSRRSSCN06 = 0;

      // Check if the current result is within 1 sigma of reference
      switch (scannerModel) {
        case 'MRSRRS_SCN05':

          // handle error criteria, propagate error by time
          deltaT = Math.abs(measurementTimestampInSeconds - refMRSRRSSCN05OldestTimestampInSeconds);

          // increase sigma by 0.1 per 3 hours
          sigmaIncreaseMRSRRSSCN05 = (countMRSRRSSCN05 * deltaT / (3 * 3600) * 0.1) ** (1 / 2);

          // calculate min and max values
          const referenceMinMRSRRSSCN05 = refValueMRSRRSSCN05 - (refValueMRSRRSSCN05Std + sigmaIncreaseMRSRRSSCN05) / 2;
          const referenceMaxMRSRRSSCN05 = refValueMRSRRSSCN05 + (refValueMRSRRSSCN05Std + sigmaIncreaseMRSRRSSCN05) / 2;

          // calculate deviations
          const minDeviationMRSRRSSCN05 = currentAoxMin - referenceMaxMRSRRSSCN05;
          const avgDeviationMRSRRSSCN05 = currentAoxResult - refValueMRSRRSSCN05;
          const maxDeviationMRSRRSSCN05 = currentAoxMax - referenceMinMRSRRSSCN05;

          // set deviation limit flags
          const deviationMinLimitFlagMRSRRSSCN05 = Math.abs(minDeviationMRSRRSSCN05) < deviationMinLimit;
          const deviationAvgLimitFlagMRSRRSSCN05 = Math.abs(avgDeviationMRSRRSSCN05) < deviationAvgLimit;
          const deviationMaxLimitFlagMRSRRSSCN05 = Math.abs(maxDeviationMRSRRSSCN05) < deviationMaxLimit;


          // set resulting flag
          const resultingFlagMRSRRSSCN05 = `${deviationMinLimitFlagMRSRRSSCN05}_${deviationAvgLimitFlagMRSRRSSCN05}_${deviationMaxLimitFlagMRSRRSSCN05}`;

          // write finale values
          pipelineConfig.setState('referenceAoxResult', roundValueToTwoDigests(refValueMRSRRSSCN05), 'temp');
          pipelineConfig.setState('referenceAoxStd', roundValueToTwoDigests(refValueMRSRRSSCN05Std), 'temp');
          pipelineConfig.setState('referenceAoxStdTime', roundValueToTwoDigests(sigmaIncreaseMRSRRSSCN05), 'temp');
          pipelineConfig.setState('referenceMin', roundValueToTwoDigests(referenceMinMRSRRSSCN05), 'temp');
          pipelineConfig.setState('referenceMax', roundValueToTwoDigests(referenceMaxMRSRRSSCN05), 'temp');
          pipelineConfig.setState('deviationMin', roundValueToTwoDigests(minDeviationMRSRRSSCN05), 'temp');
          pipelineConfig.setState('deviationAvg', roundValueToTwoDigests(avgDeviationMRSRRSSCN05), 'temp');
          pipelineConfig.setState('deviationMax', roundValueToTwoDigests(maxDeviationMRSRRSSCN05), 'temp');
          pipelineConfig.setState('deviationMinLimitFlag', deviationMinLimitFlagMRSRRSSCN05, 'temp');
          pipelineConfig.setState('deviationAvgLimitFlag', deviationAvgLimitFlagMRSRRSSCN05, 'temp');
          pipelineConfig.setState('deviationMaxLimitFlag', deviationMaxLimitFlagMRSRRSSCN05, 'temp');
          pipelineConfig.setState('deviationResultingFlag', resultingFlagMRSRRSSCN05, 'temp');


          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
          return of(true);

        case 'MRSRRS_SCN06':
          // handle error criteria, propagate error by time
          deltaT = Math.abs(measurementTimestampInSeconds - refMRSRRSSCN06OldestTimestampInSeconds);

          // increase sigma by 0.1 per 3 hours
          sigmaIncreaseMRSRRSSCN06 = (countMRSRRSSCN06 * deltaT / (3 * 3600) * 0.1) ** (1 / 2);

          // calculate min and max values
          const referenceMinMRSRRSSCN06 = refValueMRSRRSSCN06 - (refValueMRSRRSSCN06Std + sigmaIncreaseMRSRRSSCN06) / 2;
          const referenceMaxMRSRRSSCN06 = refValueMRSRRSSCN06 + (refValueMRSRRSSCN06Std + sigmaIncreaseMRSRRSSCN06) / 2;

          // calculate deviations
          const minDeviationMRSRRSSCN06 = currentAoxMin - referenceMaxMRSRRSSCN06;
          const avgDeviationMRSRRSSCN06 = currentAoxResult - refValueMRSRRSSCN06;
          const maxDeviationMRSRRSSCN06 = currentAoxMax - referenceMinMRSRRSSCN06;

          // set deviation limit flags
          const deviationMinLimitFlagMRSRRSSCN06 = Math.abs(minDeviationMRSRRSSCN06) < deviationMinLimit;
          const deviationAvgLimitFlagMRSRRSSCN06 = Math.abs(avgDeviationMRSRRSSCN06) < deviationAvgLimit;
          const deviationMaxLimitFlagMRSRRSSCN06 = Math.abs(maxDeviationMRSRRSSCN06) < deviationMaxLimit;

          // set resulting flag
          const resultingFlagMRSRRSSCN06 = `${deviationMinLimitFlagMRSRRSSCN06}_${deviationAvgLimitFlagMRSRRSSCN06}_${deviationMaxLimitFlagMRSRRSSCN06}`;

          // write finale values
          pipelineConfig.setState('referenceAoxResult', roundValueToTwoDigests(refValueMRSRRSSCN06), 'temp');
          pipelineConfig.setState('referenceAoxStd', roundValueToTwoDigests(refValueMRSRRSSCN06Std), 'temp');
          pipelineConfig.setState('referenceAoxStdTime', roundValueToTwoDigests(sigmaIncreaseMRSRRSSCN06), 'temp');
          pipelineConfig.setState('referenceMin', roundValueToTwoDigests(referenceMinMRSRRSSCN06), 'temp');
          pipelineConfig.setState('referenceMax', roundValueToTwoDigests(referenceMaxMRSRRSSCN06), 'temp');
          pipelineConfig.setState('deviationMin', roundValueToTwoDigests(minDeviationMRSRRSSCN06), 'temp');
          pipelineConfig.setState('deviationAvg', roundValueToTwoDigests(avgDeviationMRSRRSSCN06), 'temp');
          pipelineConfig.setState('deviationMax', roundValueToTwoDigests(maxDeviationMRSRRSSCN06), 'temp');
          pipelineConfig.setState('deviationMinLimitFlag', deviationMinLimitFlagMRSRRSSCN06, 'temp');
          pipelineConfig.setState('deviationAvgLimitFlag', deviationAvgLimitFlagMRSRRSSCN06, 'temp');
          pipelineConfig.setState('deviationMaxLimitFlag', deviationMaxLimitFlagMRSRRSSCN06, 'temp');
          pipelineConfig.setState('deviationResultingFlag', resultingFlagMRSRRSSCN06, 'temp');

          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
          return of(true);
      }

      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFail,'onFail', pipelineConfig.stage);
      return of(true);
    });
  }

  /**
   * Prepare the result pipeline for Precise AOX, a single AOX result, or HRV/Vitality.
   * Only a series with at least two AOX measurements performs a VWS pre-request;
   * local modes prepare their result without network access and store the shared context and status.
   */
  preRequestPreciseAox = (pipelineConfig: PipelineStageConfig): OperatorFunction<StatusObject, boolean> => {
    return switchMap(() => {
      const prepared = this.preparePreciseAoxUpload(pipelineConfig);
      if (!prepared) {
        return of(true);
      }
      if (prepared.mode === 'error') {
        return of(null).pipe(switchMap(async () => {
          await this.synchronizePreciseLicense(pipelineConfig, prepared.burnTheEvidence);
          pipelineConfig.executeStage(
            pipelineConfig?.stageConfig?.eventTargets?.onErrorNoMeasurement ?? prepared.errorTarget,
            prepared.errorTarget,
            pipelineConfig.stage,
          );
          return true;
        }));
      }
      if (prepared.mode !== 'remote') {
        this.setPreciseUploadState(pipelineConfig, prepared, {
          phase: 'preRequest',
          phases: this.createLocalPreciseUploadPhases(),
          attempt: 0,
          maxRetries: 0,
          routingServer: undefined,
          recommendedServer: undefined,
          retryPhase: undefined,
          error: undefined,
        });
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        return of(true);
      }
      const routingServers = this.scanGenericService.getAoxUploadServers();
      const routingCandidates = this.createPreciseRoutingCandidates(
        routingServers,
        pipelineConfig.state?.persistentValues?.vwsPreRequestTimeoutMs,
        pipelineConfig.state?.persistentValues?.vwsPreRequestMaxTimeoutAttempts,
      );
      const context = {
        ...prepared,
        mode: 'remote',
        routingServers,
        routingCandidates,
        routingIndex: 0,
        maxPreRequestAttempts: routingCandidates.length ? routingCandidates[routingCandidates.length - 1].round + 1 : 0,
        attemptedRoutingServers: [],
        attemptedRecommendedServers: [],
        route: undefined,
        result: undefined,
      };
      addMeasurementToLicense(
        'aox_precise', 1, pipelineConfig.state.persistentValues, pipelineConfig.setState, !context.burnTheEvidence
      );
      this.setPreciseUploadState(pipelineConfig, context, {
        phase: 'preRequest', phases: this.createPreciseUploadPhases('preRequest'),
        attempt: 1, maxRetries: context.maxPreRequestAttempts, routingServer: undefined,
      });
      return this.requestNextPreciseRoute(
        pipelineConfig, context, pipelineConfig.getState(State.DEMO_MODE), 0,
        new Error('No VWS routing server available.')
      ).pipe(
        switchMap(({route, index}: any) => {
          context.route = route;
          context.routingIndex = index;
          context.attemptedRecommendedServers = [route.recommended_server];
          this.setPreciseUploadPhase(pipelineConfig, 'preRequest', 'completed');
          this.setPreciseUploadState(pipelineConfig, context, {
            phase: 'serverSelected',
            phases: this.withPreciseUploadPhase(pipelineConfig, 'serverSelected', 'completed'),
            recommendedServer: route.recommended_server, waitingTimeMs: route.waiting_time_ms,
            retryPhase: undefined, error: undefined,
          });
          this.logPreciseNetworkSummary(pipelineConfig, 'preRequest', 'success', context, route.transportHttpStatus);
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
          return of(true);
        }),
        catchError(error => this.failPreciseUpload(pipelineConfig, 'preRequest', error))
      );
    });
  }

  /**
   * Upload a remote Precise AOX series to the VWS selected by preRequestPreciseAox.
   * Upload retries and routing-server changes apply only to remote mode; single AOX
   * and HRV/Vitality modes skip the HTTP request and continue to result processing.
   */
  uploadRawDataPreciseAox = (pipelineConfig: PipelineStageConfig): OperatorFunction<StatusObject, boolean> => {
    return switchMap(() => {
      const context = pipelineConfig.state?.temp?.aoxPreciseUploadContext;
      if (context?.mode === 'singleAox' || context?.mode === 'hrv') {
        this.setPreciseUploadStatus(pipelineConfig, {
          phase: 'resultProcessing',
          phases: this.withPreciseUploadPhase(pipelineConfig, 'resultProcessing', 'active'),
        });
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        return of(true);
      }
      if (!context?.route || !context?.additionalData) {
        return this.failPreciseUpload(pipelineConfig, 'rawData', new Error('Missing Precise AOX upload context.'));
      }
      const demoMode = pipelineConfig.getState(State.DEMO_MODE);
      const language = pipelineConfig.getState(State.SELECTED_LANGUAGE);
      return this.uploadPreciseAoxToRecommendedServer(pipelineConfig, context, demoMode, language).pipe(
        catchError(error => error === 'license' ? throwError(error) : this.reroutePreciseAoxUpload(
          pipelineConfig, context, demoMode, language, (context.routingIndex ?? 0) + 1, error
        )),
        switchMap((event: HttpEvent<PreciseAoxMeasurementResult>) => {
          if (event.type === HttpEventType.Sent) {
            this.setPreciseUploadStatus(pipelineConfig, { phase: 'rawDataSending' });
            return of(false);
          }
          if (event.type === HttpEventType.UploadProgress) {
            this.setPreciseUploadPhase(pipelineConfig, 'rawDataSending', 'active', { phase: 'rawDataSending' });
            if (event.total && event.loaded === event.total) {
              this.setPreciseUploadPhase(pipelineConfig, 'rawDataSending', 'completed');
              this.setPreciseUploadPhase(pipelineConfig, 'rawDataSent', 'completed');
              this.setPreciseUploadPhase(pipelineConfig, 'resultsRetrieving', 'active', { phase: 'resultsRetrieving' });
            }
            return of(false);
          }
          if (event.type === HttpEventType.DownloadProgress) {
            this.setPreciseUploadPhase(pipelineConfig, 'resultsRetrieving', 'active', { phase: 'resultsRetrieving' });
            return of(false);
          }
          if (event.type === HttpEventType.Response) {
            context.result = event.body;
            this.setPreciseUploadPhase(pipelineConfig, 'rawDataSending', 'completed');
            this.setPreciseUploadPhase(pipelineConfig, 'rawDataSent', 'completed');
            this.setPreciseUploadPhase(pipelineConfig, 'resultsRetrieving', 'completed');
            this.setPreciseUploadState(pipelineConfig, context, {
              phase: 'resultRetrieved',
              phases: this.withPreciseUploadPhase(pipelineConfig, 'resultRetrieved', 'completed'),
            });
            this.logPreciseNetworkSummary(pipelineConfig, 'rawDataUpload', 'success', context, event.status);
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
            return of(true);
          }
          return of(false);
        }),
        filter(result => result),
        catchError(error => this.failPreciseUpload(pipelineConfig, 'rawData', error))
      );
    });
  }

  /**
   * Process the remote Precise AOX response or a locally prepared single AOX/HRV result.
   * Stores the corresponding result values, writes CSV data where applicable, and synchronizes
   * the license unless burnTheEvidence is enabled.
   */
  processUploadResultPreciseAox = (pipelineConfig: PipelineStageConfig): OperatorFunction<StatusObject, boolean> => {
    return switchMap(() => {
      const context = pipelineConfig.state?.temp?.aoxPreciseUploadContext;
      if (!context?.result) {
        return this.failPreciseUpload(pipelineConfig, 'resultProcessing', new Error('Missing Precise AOX upload result.'));
      }
      return of(null).pipe(switchMap(async () => {
        this.setPreciseUploadPhase(pipelineConfig, 'resultProcessing', 'active', { phase: 'resultProcessing' });
        if (context.mode === 'remote') {
          const result = context.result as PreciseAoxMeasurementResult;
          processMeasurementPreciseAox(result, context.aoxData);
          result.aox = result.precise_aox;
          result.aox_int_result = result.precise_aox_floor_result;
          result.aox_decimal = Math.round(result.aox * 10) / 10;
          if (!context.burnTheEvidence) {
            this.fileManagerService.addToCsv(result, pipelineConfig, undefined, pipelineConfig.state.additionalDataStore ?? {});
          }
          pipelineConfig.setState('result', result, 'temp');
          pipelineConfig.setState('resultAox', result, 'temp');
          pipelineConfig.setState('resultString', resultToString(result, pipelineConfig.scanner.serial), 'temp');
        } else if (context.mode === 'singleAox') {
          const result = context.result as AoxMeasurementResult;
          if (!result.aox_int_result) {
            result.aox_int_result = Math.floor(result.aox);
          }
          pipelineConfig.setState('result', result, 'temp');
          pipelineConfig.setState('resultString', resultToString(result, pipelineConfig.scanner.serial), 'temp');
        } else if (context.mode === 'hrv') {
          const result = context.result;
          const preciseResult = context.preciseResult as PreciseAoxMeasurementResult | undefined;
          if (preciseResult?.series_errors) {
            result.series_errors = preciseResult.series_errors;
            if (context.aoxData.length > 0) {
              processMeasurementPreciseAox(result, context.aoxData);
            }
          }
          pipelineConfig.setState('result', result, 'temp');
        }
        await this.synchronizePreciseLicense(pipelineConfig, context.burnTheEvidence);
        this.setPreciseUploadPhase(pipelineConfig, 'resultProcessing', 'completed', { phase: 'completed' });
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        return true;
      }));
    });
  }

  private preparePreciseAoxUpload = (pipelineConfig: PipelineStageConfig): any | undefined => {
    const aoxData: AoxMeasurementResult[] = pipelineConfig.getState(State.AOX_MEASUREMENT_RESULTS) ?? [];
    const hrvData = pipelineConfig.getState(State.HRV_MEASUREMENT_RESULT);
    const burnTheEvidence = !!pipelineConfig.stageConfig.burnTheEvidence;
    if (hrvData) {
      const preciseResult: PreciseAoxMeasurementResult | undefined = pipelineConfig.state?.temp?.resultAox;
      if (preciseResult?.series_errors) {
        addMeasurementToLicense(
          aoxData.length > 0 ? 'vitality' : 'hrv',
          1,
          pipelineConfig.state.persistentValues,
          pipelineConfig.setState,
          !burnTheEvidence,
        );
      }
      return { mode: 'hrv', aoxData, result: hrvData, preciseResult, burnTheEvidence };
    }
    if (aoxData.length < 1) {
      return { mode: 'error', errorTarget: 'noMeasurement', burnTheEvidence };
    }
    if (aoxData.length === 1) {
      addMeasurementToLicense('aox', 1, pipelineConfig.state.persistentValues, pipelineConfig.setState, !burnTheEvidence);
      return { mode: 'singleAox', aoxData, result: aoxData[0], burnTheEvidence };
    }
    const serials = aoxData
      .map(entry => entry?.['status_before']?.serial ?? '')
      .filter(entry => entry !== '')
      .filter((value, index, values) => values.indexOf(value) === index);
    if (serials.length !== 1) {
      return { mode: 'error', errorTarget: 'badMeasurements', burnTheEvidence };
    }
    const additionalData: any = {
      series_results: aoxData.map(entry => entry.aox),
      series_q: aoxData.map(entry => entry.aox_q),
      series_uuid: aoxData.map(entry => entry.uuid),
      deleted_uuid: pipelineConfig.state.temp?.AOX_DATA_DELETED ?? [],
      app_version: environment.appVersion,
      config: environment.appDesignName,
      localTimestamp: new Date(Date.now()).toString(),
      localLog: getLogs(),
      series_errors: this.getErrorDict(aoxData),
      burn_the_evidence: burnTheEvidence,
    };
    if (pipelineConfig.state.additionalData) {
      Object.assign(additionalData, pipelineConfig.state.additionalData);
    }
    return { mode: 'remote', serial: serials[0], aoxData, additionalData, burnTheEvidence };
  }

  private uploadPreciseAoxToRecommendedServer = (
    pipelineConfig: PipelineStageConfig, context: any, demoMode: boolean, language: string
  ): Observable<HttpEvent<PreciseAoxMeasurementResult>> => {
    this.setPreciseUploadStatus(pipelineConfig, {
      phase: 'rawDataSending', attempt: 1, maxRetries: this.getPreciseUploadMaxRetries(pipelineConfig), retryPhase: undefined, error: undefined,
      phases: this.withPreciseUploadPhase(pipelineConfig, 'rawDataSending', 'active'),
    });
    return this.scanGenericService.uploadPreciseAoxToSelectedServer(
      demoMode, context.serial, context.route.waiting_time_ms, context.route.recommended_server, language, context.additionalData
    ).pipe(this.retryPreciseRequest(pipelineConfig, 'rawData'));
  }

  private reroutePreciseAoxUpload = (
    pipelineConfig: PipelineStageConfig, context: any, demoMode: boolean, language: string, routingIndex: number, lastError: any
  ): Observable<HttpEvent<PreciseAoxMeasurementResult>> => this.requestNextPreciseRoute(
    pipelineConfig, context, demoMode, routingIndex, lastError
  ).pipe(switchMap(({route, index}: any) => {
    context.route = route;
    context.routingIndex = index;
    context.attemptedRecommendedServers = [...(context.attemptedRecommendedServers ?? []), route.recommended_server];
    this.setPreciseUploadPhase(pipelineConfig, 'preRequest', 'completed');
    this.setPreciseUploadState(pipelineConfig, context, {
      phase: 'serverSelected', phases: this.withPreciseUploadPhase(pipelineConfig, 'serverSelected', 'completed'),
      recommendedServer: route.recommended_server, waitingTimeMs: route.waiting_time_ms, retryPhase: undefined, error: undefined,
    });
    return this.uploadPreciseAoxToRecommendedServer(pipelineConfig, context, demoMode, language).pipe(
      catchError(error => error === 'license' ? throwError(error) : this.reroutePreciseAoxUpload(
        pipelineConfig, context, demoMode, language, index + 1, error
      ))
    );
  }));

  private requestNextPreciseRoute = (
    pipelineConfig: PipelineStageConfig, context: any, demoMode: boolean, routingIndex: number, lastError: any
  ): Observable<any> => {
    const candidates = context.routingCandidates ?? [];
    if (routingIndex >= candidates.length) {
      return throwError(lastError);
    }
    const candidate = candidates[routingIndex];
    const isRetry = routingIndex > 0;
    context.attemptedRoutingServers = [...(context.attemptedRoutingServers ?? []), candidate.server];
    this.setPreciseUploadStatus(pipelineConfig, {
      phase: 'preRequest', attempt: candidate.round + 1,
      maxRetries: context.maxPreRequestAttempts ?? candidate.round + 1,
      routingServer: candidate.server, retryPhase: isRetry ? 'preRequest' : undefined,
      error: isRetry ? this.preciseUploadError(lastError) : undefined,
      phases: this.withPreciseUploadPhase(pipelineConfig, 'preRequest', 'active'),
    });
    return this.scanGenericService.requestPreciseAoxUploadServerOnServer(
      demoMode, context.serial, candidate.server, candidate.timeoutMs
    ).pipe(
      switchMap((route: any) => (context.attemptedRecommendedServers ?? []).includes(route.recommended_server)
        ? throwError(new Error('VWS returned an already attempted recommended server.'))
        : of({route, index: routingIndex})
      ),
      catchError(error => error === 'license' ? throwError(error) : this.requestNextPreciseRoute(
        pipelineConfig, context, demoMode, routingIndex + 1, error
      ))
    );
  }

  private createPreciseRoutingCandidates = (
    servers: string[], configuredTimeoutMs: any, configuredMaxTimeoutAttempts: any
  ): any[] => {
    const configuredTimeout = Number(configuredTimeoutMs);
    const maximumTimeoutMs = Number.isFinite(configuredTimeout) && configuredTimeout > 0 ? configuredTimeout : 10000;
    const maximumTimeoutAttempts = this.getPrecisePreRequestMaxTimeoutAttempts(configuredMaxTimeoutAttempts);
    const timeouts = [500, 2000]
      .filter(timeoutMs => timeoutMs < maximumTimeoutMs)
      .concat(Array(maximumTimeoutAttempts).fill(maximumTimeoutMs));
    return timeouts.reduce((candidates: any[], timeoutMs, round) => {
      servers.forEach(server => candidates.push({server, timeoutMs, round}));
      return candidates;
    }, []);
  }

  private retryPreciseRequest = <T>(pipelineConfig: PipelineStageConfig, phase: string): OperatorFunction<T, T> => retryWhen(errors => errors.pipe(
    scan((attempt: number, error: any) => {
      const maxRetries = this.getPreciseUploadMaxRetries(pipelineConfig);
      if (attempt >= maxRetries || error === 'license') {
        throw error;
      }
      this.setPreciseUploadStatus(pipelineConfig, {
        phase: 'retrying', retryPhase: phase, attempt: attempt + 2, maxRetries,
        error: this.preciseUploadError(error),
        phases: this.withPreciseUploadPhase(pipelineConfig, phase === 'rawData' ? 'rawDataSending' : 'preRequest', 'active'),
      });
      return attempt + 1;
    }, 0),
    delay(1000),
  ));

  private getPrecisePreRequestMaxTimeoutAttempts = (value: any): number => {
    const attempts = Number(value);
    return Number.isInteger(attempts) && attempts > 0 ? attempts : 1;
  }

  private getPreciseUploadMaxRetries = (pipelineConfig: PipelineStageConfig): number => {
    const retries = Number(pipelineConfig.state?.persistentValues?.vwsUploadMaxRetries);
    return Number.isInteger(retries) && retries > 0 ? retries : 5;
  }

  private failPreciseUpload = (pipelineConfig: PipelineStageConfig, phase: string, error: any): Observable<boolean> => {
    this.setPreciseUploadStatus(pipelineConfig, {
      phase: 'failed', failedPhase: phase, error: this.preciseUploadError(error),
      phases: this.withPreciseUploadPhase(pipelineConfig, phase === 'rawData' ? 'rawDataSending' : phase, 'failed'),
    });
    if (phase === 'preRequest' || phase === 'rawData') {
      this.logPreciseNetworkSummary(
        pipelineConfig,
        phase === 'preRequest' ? 'preRequest' : 'rawDataUpload',
        'failed',
        pipelineConfig.state?.temp?.aoxPreciseUploadContext,
        getUploadHttpStatus(error),
        error,
      );
    }
    pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
    return of(true);
  }

  private logPreciseNetworkSummary = (
    pipelineConfig: PipelineStageConfig,
    operation: 'preRequest' | 'rawDataUpload',
    outcome: 'success' | 'failed',
    context: any,
    httpStatus: number | null | undefined,
    error?: any,
  ) => {
    const status = pipelineConfig.state?.temp?.aoxPreciseUploadStatus ?? {};
    const nextStage = outcome === 'success'
      ? pipelineConfig.stageConfig?.eventTargets?.onSuccess
      : pipelineConfig.stageConfig?.eventTargets?.onError;
    logUploadNetworkSummary(pipelineConfig, nextStage, {
      measurement: 'aoxPrecise',
      operation,
      outcome,
      attempt: status.attempt,
      maxRetries: status.maxRetries,
      httpStatus: httpStatus ?? null,
      routingServer: status.routingServer,
      recommendedServer: status.recommendedServer,
      attemptedRoutingServers: context?.attemptedRoutingServers ?? [],
      attemptedRecommendedServers: context?.attemptedRecommendedServers ?? [],
      ...(error ? {error: {name: error?.name, message: error?.message ?? String(error)}} : {}),
    });
  }

  private setPreciseUploadState = (pipelineConfig: PipelineStageConfig, context: any, status: any) => {
    pipelineConfig.setState?.('aoxPreciseUploadContext', context, 'temp');
    this.setPreciseUploadStatus(pipelineConfig, status);
  }

  private setPreciseUploadStatus = (pipelineConfig: PipelineStageConfig, update: any) => {
    const current = pipelineConfig.state?.temp?.aoxPreciseUploadStatus ?? {};
    pipelineConfig.setState?.('aoxPreciseUploadStatus', {
      ...current,
      ...this.withServerDisplayNames(update),
    }, 'temp');
  }

  private withServerDisplayNames = (update: any) => {
    const status = {...update};
    for (const key of ['routingServer', 'recommendedServer']) {
      if (Object.prototype.hasOwnProperty.call(status, key)) {
        status[key] = getServerDisplayName(status[key]);
      }
    }
    return status;
  }

  private createPreciseUploadPhases = (activePhase: string) => {
    const phases = ['preRequest', 'serverSelected', 'rawDataSending', 'rawDataSent', 'resultsRetrieving', 'resultRetrieved', 'resultProcessing'];
    return phases.reduce((result, phase) => ({...result, [phase]: phase === activePhase ? 'active' : 'pending'}), {});
  }

  private createLocalPreciseUploadPhases = () => ({
    preRequest: 'skipped',
    serverSelected: 'skipped',
    rawDataSending: 'skipped',
    rawDataSent: 'skipped',
    resultsRetrieving: 'skipped',
    resultRetrieved: 'skipped',
    resultProcessing: 'pending',
  });

  private withPreciseUploadPhase = (pipelineConfig: PipelineStageConfig, phase: string, state: string) => {
    const phases = pipelineConfig.state?.temp?.aoxPreciseUploadStatus?.phases ?? this.createPreciseUploadPhases('');
    return {...phases, [phase]: state};
  }

  private setPreciseUploadPhase = (pipelineConfig: PipelineStageConfig, phase: string, state: string, update: any = {}) => {
    this.setPreciseUploadStatus(pipelineConfig, {...update, phases: this.withPreciseUploadPhase(pipelineConfig, phase, state)});
  }

  private preciseUploadError = (error: any): string => error?.message ?? error?.toString?.() ?? 'Unknown error';

  private synchronizePreciseLicense = async (pipelineConfig: PipelineStageConfig, burnTheEvidence: boolean) => {
    if (burnTheEvidence) {
      return;
    }
    const license = await sendLicense(pipelineConfig.state.persistentValues);
    if (license) {
      pipelineConfig.setState(State.LICENSE, license);
    }
  }

  /**
   * send a request to the server to combine multiple measurements into a single one
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  fetchResult = (
    pipelineConfig: PipelineStageConfig
  ): OperatorFunction<StatusObject, boolean> => {
    return switchMap(() => {
      return of({}).pipe(
        switchMap(() => {

          const burnTheEvidence = !!pipelineConfig.stageConfig.burnTheEvidence;
          const hrvData = pipelineConfig.getState(State.HRV_MEASUREMENT_RESULT);
          const aoxData: AoxMeasurementResult[] = pipelineConfig.getState(State.AOX_MEASUREMENT_RESULTS);
          if (hrvData) {

            const aoxDataPrecise: PreciseAoxMeasurementResult = pipelineConfig?.state?.temp?.resultAox;
            if (aoxDataPrecise && aoxDataPrecise?.series_errors) {
              hrvData.series_errors = aoxDataPrecise.series_errors;
              if (aoxData && aoxData.length > 0) {
                addMeasurementToLicense(
                  'vitality', 1, pipelineConfig.state.persistentValues, pipelineConfig.setState, !burnTheEvidence
                );
                processMeasurementPreciseAox(hrvData, aoxData);
              } else {
                addMeasurementToLicense('hrv', 1, pipelineConfig.state.persistentValues, pipelineConfig.setState, !burnTheEvidence);
              }
            }
            pipelineConfig.setState('result', hrvData, 'temp');
            return of('onSuccess');
          }

          if (!aoxData || aoxData.length === 0) {
            // if only hrv data exists, this is a hrv measurement, and doesn't need to combine results
          }


          // if no result exists, proceed to the onErrorNoMeasurement-stage
          if (!aoxData || aoxData.length < 1) {
            return of('noMeasurement');
          }
          // if only one aox result exists, combining it would return the same result, so it can be skipped
          if (aoxData.length === 1) {
            if (!(aoxData[0].aox_int_result)) {
              aoxData[0].aox_int_result = Math.floor(aoxData[0].aox);
            }
            pipelineConfig.setState('result', aoxData[0], 'temp');
            pipelineConfig.setState(
              'resultString',
              resultToString(aoxData[0], pipelineConfig.scanner.serial),
              'temp'
            );
            addMeasurementToLicense('aox', 1, pipelineConfig.state.persistentValues, pipelineConfig.setState, !burnTheEvidence);
            return of('onSuccess');
          }

          const seriesError = this.getErrorDict(aoxData);

          let dataToSend = {
            series_results: aoxData.map(el => el.aox),
            series_q: aoxData.map(el => el.aox_q),
            series_uuid: aoxData.map(el => el.uuid),
            deleted_uuid: pipelineConfig.state.temp?.AOX_DATA_DELETED ?? [],
            app_version: environment.appVersion,
            config: environment.appDesignName,
            localTimestamp: new Date(Date.now()).toString(),
            localLog: getLogs(),
            series_errors: seriesError,
          };
          const serials = aoxData
            .map((entry) => entry?.['status_before']?.serial ?? '')
            .filter((e) => e !== '')
            .filter((v, i, a) => a.indexOf(v) === i);

          if (serials.length !== 1) {
            if (!aoxData || aoxData.length < 1) {
              return of('badMeasurements');
            }
          }
          if (pipelineConfig.state.additionalData) {
            dataToSend = { ...dataToSend, ...pipelineConfig.state.additionalData };
          }
          addMeasurementToLicense('aox_precise', 1, pipelineConfig.state.persistentValues, pipelineConfig.setState, !burnTheEvidence);

          return this.scanGenericService.fetchResult(
            pipelineConfig.getState(State.DEMO_MODE),
            pipelineConfig.getState(State.SELECTED_LANGUAGE),
            serials[0],
            { ...dataToSend, burn_the_evidence: burnTheEvidence, localTimestamp: Date.now() },
            pipelineConfig.state?.persistentValues?.vwsPreRequestTimeoutMs,
          ).pipe(
            switchMap((res: HttpEvent<PreciseAoxMeasurementResult>) => {
              if (res.type === HttpEventType.Response) {
                const preciseAoxMeasurementResult = res.body;
                processMeasurementPreciseAox(preciseAoxMeasurementResult, aoxData);
                preciseAoxMeasurementResult.aox = preciseAoxMeasurementResult.precise_aox;
                preciseAoxMeasurementResult.aox_int_result = preciseAoxMeasurementResult.precise_aox_floor_result;
                preciseAoxMeasurementResult.aox_decimal = Math.round(preciseAoxMeasurementResult.aox * 10) / 10;
                // preciseAoxMeasurementResult.aox_q = preciseAoxMeasurementResult.precise_aox_std;

                // only write result if burnTheEvidence is false
                if (!burnTheEvidence) {
                  this.fileManagerService.addToCsv(preciseAoxMeasurementResult, pipelineConfig, undefined, pipelineConfig.state.additionalDataStore ?? {});
                }
                pipelineConfig.setState('result', preciseAoxMeasurementResult, 'temp');
                pipelineConfig.setState('resultAox', preciseAoxMeasurementResult, 'temp');
                pipelineConfig.setState(
                  'resultString',
                  resultToString(preciseAoxMeasurementResult, pipelineConfig.scanner.serial),
                  'temp'
                );
                return of([true, 'onSuccess']);
              }
              return of([false, 'onError']);
            }),
            filter(el => !!(el[0])),
            switchMap((el) => {
              return of(el[1]);
            })
          );
        }),
        first(),
        switchMap(async (target: 'onSuccess' | 'badMeasurements' | 'noMeasurement' | 'onError') => {
          const burnTheEvidence = !!pipelineConfig.stageConfig.burnTheEvidence;
          if (!burnTheEvidence) {
            const license = await sendLicense(pipelineConfig.state.persistentValues);
            if (license) {
              pipelineConfig.setState(State.LICENSE, license);
            }
          }
          switch (target) {
            case 'onSuccess': {
              pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
              break;
            }
            case 'noMeasurement': {
              pipelineConfig.executeStage(
                pipelineConfig?.stageConfig?.eventTargets?.onErrorNoMeasurement ?? 'noMeasurement',
                'noMeasurement',
                pipelineConfig.stage
              );
              break;
            }
            case 'badMeasurements': {
              pipelineConfig.executeStage(
                pipelineConfig?.stageConfig?.eventTargets?.onErrorNoMeasurement ?? 'badMeasurements',
                'badMeasurements',
                pipelineConfig.stage
              );
              break;
            }
            case 'onError':
            default: {
              pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
            }
          }
          return true;
        })
      );
    });
  }

  /**
   * finish all measurement components, and go to the result page
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  displayResults = (
    pipelineConfig: PipelineStageConfig
  ) => {
    return tap(() => {
      const step = pipelineConfig.getStep();
      const res = pipelineConfig.state.temp.result;
      if (res) {
        const url = getResult(
          res,
          step.serial,
          pipelineConfig.config,
          pipelineConfig.getState(State.SELECTED_LANGUAGE)
        );
        pipelineConfig.iab.create(url, '_system');
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        return;
      }
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onMissingResult, 'onMissingResult', pipelineConfig.stage);
    });
  }

  /**
   * finish all measurement components, and go to the result page
   * @param _pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  goToHome = (
    _pipelineConfig: PipelineStageConfig
  ) => {
    return tap(() => {
      if (this.platform.is('cordova')) {
        this.insomnia.allowSleepAgain();
      }
      this.router.navigateByUrl('/home');
    });
  }

  /**
   * handler for all errors that are not caught by the stages
   * this stage should never trigger
   * might need an overhaul / some more cases
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   * @param onExpiration on a timeout error, go to this page
   */
  handleError = (
    pipelineConfig: PipelineStageConfig,
    onExpiration?: string,
  ): OperatorFunction<HttpErrorResponse, HttpErrorResponse> => {
    return catchError((err: HttpErrorResponse) => {
      if (err instanceof TimeoutError) {
        pipelineConfig.executeStage(onExpiration, 'Timeout', pipelineConfig.stage);
        return EMPTY;
      }
      logError(err);
      pipelineConfig.executeStage(environment.appDesignSettings.homeStage, 'Error', pipelineConfig.stage);
      return EMPTY;
    });
  }

  fetchSerial = (pipelineConfig: PipelineStageConfig) => {
    return this.scanGenericService.fetchSerial(pipelineConfig);
  }

  finalizeScanner(pipelineConfig: PipelineStageConfig) {
    return this.scanGenericService.finalizeScanner(pipelineConfig);
  }
}
